// Prematch view-model normalizer.
// The PUBLIC surface (`/prematch?grouped=true`) already returns the grouped
// PrematchResponse shape ({ rows: PrematchFixture[] }). The AUTHENTICATED
// surface (`/prematch-v2`) returns a DIFFERENT shape — an ATLAS-curated
// selection envelope ({ count, curated, future_only, items, policy, source }).
// This adapter maps BOTH into the app's single normalized PrematchResponse so
// the Prematch UI renders identically regardless of source.
//
// It NEVER fabricates data: every field is read from the real payload with
// tolerant key names, and anything genuinely absent stays null.
import { PrematchResponse, PrematchFixture, PrematchMarket, SignalStatus } from "@/types/api";
import { logoFrom } from "@/components/common/TeamLogo";

function str(v: unknown): string {
  return v == null ? "" : String(v);
}
function numOrNull(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function pick<T = unknown>(o: any, keys: string[]): T | undefined {
  if (!o || typeof o !== "object") return undefined;
  for (const k of keys) if (o[k] != null && o[k] !== "") return o[k] as T;
  return undefined;
}
function statusOf(v: unknown): SignalStatus {
  const s = str(v).toUpperCase();
  return (["PENDING", "RESOLVED", "WIN", "LOSE", "UNKNOWN"] as const).includes(s as SignalStatus)
    ? (s as SignalStatus)
    : "PENDING";
}

// Map a single flat V2 selection item -> normalized PrematchMarket.
function toMarket(item: any): PrematchMarket {
  return {
    id: numOrNull(pick(item, ["id", "market_id"])) ?? 0,
    created_at: str(pick(item, ["created_at", "createdAt"])),
    fixture_id: str(pick(item, ["fixture_id", "fixtureId", "match_id"])),
    fixture_date: str(pick(item, ["fixture_date", "fixtureDate", "date", "kickoff", "start_time"])),
    minutes_to_kickoff: numOrNull(pick(item, ["minutes_to_kickoff", "minutesToKickoff"])),
    country: (pick<string>(item, ["country"]) ?? null) as string | null,
    league: (pick<string>(item, ["league", "competition"]) ?? null) as string | null,
    home_team: str(pick(item, ["home_team", "homeTeam", "home"])),
    away_team: str(pick(item, ["away_team", "awayTeam", "away"])),
    rank: numOrNull(pick(item, ["rank"])),
    market_code: str(pick(item, ["market_code", "marketCode", "market", "code"])),
    market_name: (pick<string>(item, ["market_name", "marketName", "market_label", "name"]) ?? null) as string | null,
    model_probability: numOrNull(pick(item, ["model_probability", "modelProbability", "confidence", "probability", "prob"])),
    reference_odd: numOrNull(pick(item, ["reference_odd", "referenceOdd", "odd", "reference_odds", "price"])),
    implied_probability: numOrNull(pick(item, ["implied_probability", "impliedProbability"])),
    edge: numOrNull(pick(item, ["edge", "value_edge"])),
    score: numOrNull(pick(item, ["score", "market_score", "confidence_score"])),
    bookmaker_count: numOrNull(pick(item, ["bookmaker_count", "bookmakerCount"])),
    status: statusOf(pick(item, ["status"])),
    final_home_goals: numOrNull(pick(item, ["final_home_goals"])),
    final_away_goals: numOrNull(pick(item, ["final_away_goals"])),
    won: (pick(item, ["won"]) as number | boolean | null) ?? null,
    resolved_at: (pick<string>(item, ["resolved_at"]) ?? null) as string | null,
    home_logo: logoFrom(item, "home") ?? logoFrom(item?.footystats_metrics, "home"),
    away_logo: logoFrom(item, "away") ?? logoFrom(item?.footystats_metrics, "away"),
    league_logo: logoFrom(item, "league") ?? logoFrom(item?.footystats_metrics, "league"),
    home_team_id: (pick(item, ["home_team_id", "homeTeamId"]) ?? item?.footystats_metrics?.home_team_id ?? null) as number | string | null,
    away_team_id: (pick(item, ["away_team_id", "awayTeamId"]) ?? item?.footystats_metrics?.away_team_id ?? null) as number | string | null,
    league_id: (pick(item, ["league_id", "leagueId"]) ?? item?.footystats_metrics?.competition_id ?? null) as number | string | null,
    provider: (pick<string>(item, ["provider"]) ?? item?.footystats_metrics?.provider ?? null) as string | null,
    footystats_metrics: (item?.footystats_metrics ?? item?.footystatsMetrics ?? null) as any,
  };
}

// Best pick within a fixture: highest score, else rank asc, else confidence.
function bestOf(markets: PrematchMarket[]): PrematchMarket | null {
  if (markets.length === 0) return null;
  return [...markets].sort((a, b) => {
    const sa = a.score, sb = b.score;
    if (sa != null && sb != null && sa !== sb) return sb - sa;
    const ra = a.rank, rb = b.rank;
    if (ra != null && rb != null && ra !== rb) return ra - rb;
    return (b.model_probability ?? 0) - (a.model_probability ?? 0);
  })[0];
}

// Group flat V2 selection items into PrematchFixture rows (mirrors the public
// grouped shape). Fields that don't exist upstream remain null.
function groupItems(items: any[]): PrematchFixture[] {
  const byFixture = new Map<string, PrematchMarket[]>();
  for (const raw of items) {
    const m = toMarket(raw);
    const key = m.fixture_id || `${m.home_team}-${m.away_team}-${m.fixture_date}`;
    const list = byFixture.get(key) ?? [];
    list.push(m);
    byFixture.set(key, list);
  }
  const rows: PrematchFixture[] = [];
  byFixture.forEach((markets, key) => {
    const head = markets[0];
    rows.push({
      fixture_key: key,
      fixture_id: head.fixture_id || key,
      fixture_date: head.fixture_date,
      minutes_to_kickoff: head.minutes_to_kickoff,
      country: head.country,
      league: head.league,
      home_team: head.home_team,
      away_team: head.away_team,
      status: head.status,
      won: head.won,
      markets,
      best: bestOf(markets),
      home_logo: markets.map((m) => m.home_logo).find(Boolean) ?? null,
      away_logo: markets.map((m) => m.away_logo).find(Boolean) ?? null,
      league_logo: markets.map((m) => m.league_logo).find(Boolean) ?? null,
      home_team_id: markets.map((m) => m.home_team_id).find((v) => v != null) ?? null,
      away_team_id: markets.map((m) => m.away_team_id).find((v) => v != null) ?? null,
      league_id: markets.map((m) => m.league_id).find((v) => v != null) ?? null,
      provider: markets.map((m) => m.provider).find(Boolean) ?? null,
      footystats_metrics: markets.map((m) => m.footystats_metrics).find(Boolean) ?? null,
    });
  });
  return rows;
}

// Public API: accept either surface's raw payload -> normalized PrematchResponse.
export function normalizePrematch(raw: any): PrematchResponse {
  const generated_at = str(pick(raw, ["generated_at", "generatedAt", "updated_at"]));

  // Already-grouped shape (public /prematch?grouped=true or V2 grouped) -> as-is.
  if (Array.isArray(raw?.rows)) {
    return { generated_at, rows: raw.rows as PrematchFixture[], grouped: raw.grouped ?? true };
  }

  // V2 selection envelope -> map `items`.
  const items = raw?.items ?? raw?.curated ?? raw?.selections ?? raw?.data;
  if (Array.isArray(items)) {
    // If items are already fixture-grouped (carry a markets[] array), surface the
    // real crest/id fields (from the fixture item or its first market) via the
    // SAME nested-aware logoFrom used by the cards/detail — never fabricated.
    if (items.length > 0 && Array.isArray(items[0]?.markets)) {
      const rows = (items as any[]).map((it) => {
        const m0 = (Array.isArray(it.markets) && it.markets[0]) ? it.markets[0] : null;
        const fs = it?.footystats_metrics ?? m0?.footystats_metrics ?? null;
        const gid = (keys: string[]) => {
          for (const src of [it, m0]) for (const k of keys) if (src?.[k] != null && src[k] !== "") return src[k];
          return null;
        };
        return {
          ...it,
          home_logo: logoFrom(it, "home") ?? logoFrom(m0, "home") ?? logoFrom(fs, "home"),
          away_logo: logoFrom(it, "away") ?? logoFrom(m0, "away") ?? logoFrom(fs, "away"),
          league_logo: logoFrom(it, "league") ?? logoFrom(m0, "league") ?? logoFrom(fs, "league"),
          home_team_id: gid(["home_team_id", "homeTeamId"]) ?? fs?.home_team_id ?? null,
          away_team_id: gid(["away_team_id", "awayTeamId"]) ?? fs?.away_team_id ?? null,
          league_id: gid(["league_id", "leagueId"]) ?? fs?.competition_id ?? null,
          provider: it?.provider ?? m0?.provider ?? fs?.provider ?? null,
          footystats_metrics: fs,
        };
      });
      return { generated_at, rows: rows as PrematchFixture[], grouped: true };
    }
    return { generated_at, rows: groupItems(items), grouped: true };
  }

  return { generated_at, rows: [], grouped: true };
}
